/**
 * View component for /evaluations/:evaluationId
 *
 * Displays a single evaluation from the 'byId' map in the evaluation reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import third-party libraries
import classNames from 'classnames';
import renderHTML from 'react-render-html';

// import actions
import * as courseActions from '../../course/courseActions';
import * as evaluationActions from '../evaluationActions';
import * as moduleCertificateActions from '../../moduleCertificate/moduleCertificateActions';
import * as questionActions from '../../question/questionActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';
import * as userModuleActions from '../../userModule/userModuleActions';
import * as userEvaluationActions from '../../userEvaluation/userEvaluationActions';


// import global components
import Base from "../../../global/components/BaseComponent.js.jsx";
import VideoPlayer from "../../../global/components/helpers/VideoPlayer.js.jsx";

// import module components
import EvaluationLayout from '../components/EvaluationLayout.js.jsx';
import QuestionEditor from '../../question/components/QuestionEditor.js.jsx';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';
import richContentUtils from '../../../global/utils/richContentUtils';
import videoUtils from "../../../global/utils/videoUtils.js";

// import evaluation css modules
import evaluationStyles from '../evaluationModuleStyles.css';


class PostEvaluation extends Base {
  constructor(props) {
    super(props);
    this.state = {}
    this._bind(
      '_invalidateCerts'
      , '_onVideoComplete'
    )
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    if(match.params.courseSlug) {
      // non-agenda route.  useful to have course info
      dispatch(courseActions.fetchSingleBySlug(match.params.courseSlug));
    } else {
      // want to make sure the old course info doesn't persist on agenda routes
      dispatch(courseActions.invalidateSelected())
    }
    dispatch(evaluationActions.fetchSingleIfNeeded(match.params.evaluationId))
    dispatch(userEvaluationActions.fetchSingleUserEvaluationByEvaluation(match.params.evaluationId)).then(action => {
      if(!action.success) {
        alert("Error finding current User Evaluation");
      } else if(!action.item) {
        // this is a big one. the user should NOT be able to view this without a valid "UserModule" object in the database. this could happen if they paste the url for the "conclusion" page directly in here.
        // TODO: this should redirect silently when we go live.
        alert("ATTN: You must first start the Evaluation.");
        this.props.history.push(match.url.replace(`/evaluations/${match.params.evaluationId}/post-evaluation`, '/conclusion'))
      }
    })
    dispatch(questionActions.fetchListIfNeeded('_evaluation', match.params.evaluationId));
    // dispatch(contentModuleActions.fetchSingleBySlug(match.params.contentModuleSlug));
  }

  _onVideoComplete(e) {
    console.log('video done');
    console.log(e);
  }

  _invalidateCerts() {
    const { dispatch, loggedInUser, contentModuleStore, userEvaluationStore } = this.props;
    const userEvaluation = userEvaluationStore.selected.getItem();
    dispatch(moduleCertificateActions.invalidateList( '_user', loggedInUser._id, '_contentModule', contentModuleStore.selected.id));
    dispatch(userEvaluationActions.invalidateList('_userModule', userEvaluation._userModule));
  }

  render() {
    const {
      courseStore
      , defaultQuestion
      , evaluationStore
      , match
      , questionStore
      , contentModuleStore
      , userEvaluationStore
    } = this.props;
    /**
     * use the selected.getItem() utility to pull the actual evaluation object from the map
     */
    const selectedEvaluation = evaluationStore.selected.getItem();
    const questionList = questionStore.util.getList('_evaluation', match.params.evaluationId);
    const userEvaluation = userEvaluationStore.selected.getItem();

    const isEmpty = (
      !selectedEvaluation
      || !selectedEvaluation._id
      || evaluationStore.selected.didInvalidate
      // || courseStore.selected.isFetching
      // || !courseStore.selected.id
      || !questionList
      || !userEvaluation
    );

    let sortedQuestions;
    if(!isEmpty) {
      sortedQuestions = filterUtils.sortByOrderWeight(questionList);
    }

    let postEvalHTML = null;
    if(selectedEvaluation && selectedEvaluation.postEvalContent && selectedEvaluation.postEvalContent.blocks && selectedEvaluation.postEvalContent.blocks.length > 0) {
      postEvalHTML = richContentUtils.editorState(selectedEvaluation.postEvalContent);
    }
    const statusBarClass = classNames(
      'status-bar'
      , {
        '-failed': userEvaluation && userEvaluation.status == 'failed'
        , '-passed': userEvaluation && userEvaluation.status == 'passed'
      }
    )

    const linkClass = classNames(
      "yt-btn slp large block"
    )
    return (
      <EvaluationLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container post-evaluation">
              {isEmpty ?
                (evaluationStore.selected.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
                :
                <div style={{ opacity: evaluationStore.selected.isFetching ? 0.5 : 1 }}>
                  <div className="yt-row center-horiz">
                    <div className="yt-col full s_80 m_75 l_60 xl_50">
                      <h3> Review your exam </h3>
                      <div className={statusBarClass}>
                        { userEvaluation.status === "failed" ?
                          <strong>Evaluation failed</strong>
                          : userEvaluation.status === "passed" ?
                          <strong>Success!</strong>
                          :
                          <strong>{userEvaluation.status}</strong>
                        }
                      </div>
                      <VideoPlayer
                        isAutoPlay={true}
                        onVideoComplete={this._onVideoComplete}
                        playerId={selectedEvaluation._id + '_intro'}
                        playlist={videoUtils.returnPlaylist(selectedEvaluation.postEvalVidPath, selectedEvaluation.postEvalPosterPath, selectedEvaluation.postTracks)}
                        tracking={{
                          _course: courseStore.selected.id
                          , _evaluation: this.props.match.params.evaluationId
                          , _contentModule: selectedEvaluation._contentModule
                          , description: "user-evaluation-post-content"
                        }}
                        />
                      <div className="content-container">
                        <h1> { selectedEvaluation.title } </h1>
                        { postEvalHTML ? renderHTML(postEvalHTML) : '' }
                        <Link
                          className={linkClass}
                          onClick={this._invalidateCerts}
                          to={`${match.url.replace(`/evaluations/${match.params.evaluationId}/post-evaluation`, '/conclusion')}`}
                        >
                          <i className="fas fa-arrow-left"/>
                          { userEvaluation && userEvaluation.status == 'passed' ?
                            <span>  View your certificate!</span>
                            :
                            <span>  Click here to try again. </span>
                          }
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          </section>
        </div>
      </EvaluationLayout>
    )
  }
}

PostEvaluation.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store, ownProps) => {
  /**
   * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
   * differentiated from the React component's internal state
   */

  return {
    courseStore: store.course
    , defaultQuestion: store.question.defaultItem
    , evaluationStore: store.evaluation
    , loggedInUser: store.user.loggedIn.user
    , questionStore: store.question
    , contentModuleStore: store.contentModule
    , userEvaluationStore: store.userEvaluation
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(PostEvaluation)
);



// WEBPACK FOOTER //
// modules/evaluation/views/PostEvaluation.js.jsx